import React from 'react';

import { Typography, DialogContent, Card } from '@mui/material';

import Box from '@mui/material/Box';

import { useTranslation } from 'next-i18next';

import Dialog from 'src/components/ui/Dialog';
import DialogTitle from 'src/components/ui/DialogTitle';
import Divider from 'src/components/ui/Divider';
import Button from 'src/components/ui/Button';
import StoryReactionCard from 'src/components/news/StoryReactionCard';
import Loader from 'src/components/ui/Loader';
import Image from 'src/components/ui/Image';

import { colours } from 'src/constants/colours';

import { useStoryQuery, useStoryReactionsQuery } from 'src/hooks/__generated__/queries';
import type { StoriesQuery, StoryReactionsQuery } from 'src/types/__generated__/graphql';
import { isBottomDiv } from 'src/utils/isBottom';
import useAlerts from 'src/hooks/useAlerts';
import { styledComponent } from 'src/utils/styled';

const StyledDialog = styledComponent(Dialog)`
    .MuiDialog-paper {
        position: relative;
        height: 40.75rem;
    }
`;

const StyledDialogTitle = styledComponent(DialogTitle)`
    padding: 1.5rem 1.5rem 0.5rem 1.5rem;
`;

const StyledDialogContent = styledComponent(DialogContent)`
    display: flex;
    flex-direction: column;
    justify-content: center;
    height: 8.125rem;
`;

const StyledDialogCards = styledComponent(DialogContent)`
    display: flex;
    flex-direction: column;
    height: 10rem;
    overflow-x: hidden;
`;

const StyledTypography = styledComponent(Typography)`
    font-size: 1.813rem;
    text-align: center;
`;

const StyledCardStory = styledComponent(Card)`
    max-width: 4.563rem;
    max-height: 8.125rem;
    background: ${colours.black.O04};
    margin: 0.25rem 0;
`;

StyledDialog.defaultProps = {
    maxWidth: 'xs',
    fullWidth: true,
};

const defaultLimit = 10;

interface Props {
    story: ArrayElement<NonNullable<StoriesQuery['stories']>>;
    open: boolean;
    onClose?: () => void;
    onCancel?: () => void;
}

const StoryReactionModal: React.FC<Props> = ({ story, open, onClose, onCancel }) => {
    const { t } = useTranslation(['news']);
    const { addAlert } = useAlerts();

    const { data: storyData, loading: loadingStoryData } = useStoryQuery({
        variables: {
            storyId: story.id,
        },
        skip: !story.id,
    });

    const {
        data: storyReactions,
        fetchMore: fetchMoreStoryReactions,
        loading: loadingStoryReactions,
    } = useStoryReactionsQuery({
        variables: {
            storyId: story.id,
            take: defaultLimit,
        },
        skip: !story.id,
    });

    const handleScroll = async (ev: React.UIEvent<HTMLDivElement, UIEvent>) => {
        const bottom = isBottomDiv(ev.currentTarget);
        const allDataLoaded = Number(storyReactions?.storyReactions?.length) >= story.numberOfReactions;
        if (bottom && !loadingStoryReactions && !allDataLoaded) {
            try {
                const actualDataLength = storyReactions?.storyReactions?.length;
                await fetchMoreStoryReactions({
                    variables: {
                        skip: actualDataLength,
                    },
                    updateQuery(prev, { fetchMoreResult }: { fetchMoreResult?: StoryReactionsQuery }) {
                        if (!fetchMoreResult) return prev;

                        const prevItems = prev.storyReactions ?? [];
                        const newItems = fetchMoreResult.storyReactions ?? [];
                        return {
                            storyReactions: [...prevItems, ...newItems],
                        };
                    },
                });
            } catch (error: unknown) {
                addAlert({
                    type: 'error',
                    message: (error as Error).message,
                });
            }
        }
    };

    const itemImage = storyData?.story?.medium.url;
    const isLoading = loadingStoryReactions;
    const numberOfReactions = story.numberOfReactions;

    return (
        <StyledDialog onClose={onClose} open={open} fullWidth>
            <StyledDialogTitle onClose={onCancel}>
                <Box display="flex" flexDirection="row" alignItems="center">
                    <StyledTypography variant="h1">
                        {t('news:allReactions', {
                            count: numberOfReactions,
                        })}
                    </StyledTypography>
                </Box>
            </StyledDialogTitle>
            <StyledDialogContent>
                <Box display="flex" justifyContent="center">
                    <StyledCardStory>
                        {itemImage && (
                            <Image
                                src={itemImage}
                                alt="story-reaction"
                                width={73}
                                height={130}
                                style={{ objectFit: 'cover' }}
                            />
                        )}
                        {loadingStoryData && <Loader />}
                    </StyledCardStory>
                </Box>
                <StyledDialogCards onScroll={handleScroll}>
                    {storyReactions?.storyReactions?.map((reaction) => (
                        <StoryReactionCard key={reaction.id} storyReaction={reaction} />
                    ))}
                    {isLoading && <Loader />}
                </StyledDialogCards>

                <Box height="4rem">
                    <Divider />
                    <Box display="flex" mt="0.5rem" flexDirection="row" justifyContent="flex-end">
                        <Button variant="outlined" size="large" onClick={onCancel}>
                            {t('common:leave')}
                        </Button>
                    </Box>
                </Box>
            </StyledDialogContent>
        </StyledDialog>
    );
};

export default StoryReactionModal;
